import React, { useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  useGetPosInvoiceQuery,
  useGetPosOrderQuery,
  useGetPosOrderInvoicesQuery,
  useProcessPosInvoicePaymentMutation,
  useProcessPosOrderPaymentMutation,
  usePosSeatHeartbeatMutation,
} from "../../store/api";
import { api } from "../../store/api";
import PaymentMethodSelector from "../../components/pos/PaymentMethodSelector";
import OrderStatusBadge from "../../components/pos/OrderStatusBadge";
import Alert from "../../components/common/Alert";
import Button from "../../components/common/Button";
import PrintReceiptAction from "../../components/pos/PrintReceiptAction";
import { getPosErrorMessage } from "../../utils/posErrorHandler";
import {
  buildPaymentSignature,
  isRetryablePaymentError,
  nextIdempotencyState,
} from "../../utils/paymentIdempotency";
import { useOrderRealtime } from "../../hooks/usePosRealtime";
import { normalizeMoneyAmount } from "../../utils/money";
import { useStationFeature } from "../../hooks/useStationFeature";

/**
 * Payment Page
 * Handles both invoice and full order payments with real-time updates
 */
const Payment = () => {
  const { invoiceId, orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [realtimeUpdate, setRealtimeUpdate] = useState(null);
  const [selectedTipAmount, setSelectedTipAmount] = useState(0);
  const lastInvoicePaymentAttemptRef = useRef(null);
  const { tipConfig } = useStationFeature();

  // Determine payment type
  const isInvoicePayment = !!invoiceId;
  const targetOrderId = orderId || location.state?.orderId;
  const [resolvedOrderId, setResolvedOrderId] = useState(targetOrderId || null);

  // Real-time updates for payment status
  useOrderRealtime(resolvedOrderId, (payload) => {
    if (payload.status === "APPROVED" || payload.status === "COMPLETED") {
      setRealtimeUpdate(payload);
      setTimeout(() => setRealtimeUpdate(null), 3000);
    }
  });

  // Fetch data based on payment type
  const { data: invoice, isLoading: invoiceLoading } = useGetPosInvoiceQuery(
    invoiceId,
    { skip: !isInvoicePayment },
  );
  const { data: order, isLoading: orderLoading } = useGetPosOrderQuery(
    resolvedOrderId,
    { skip: !resolvedOrderId },
  );
  const { data: orderInvoicesData, isLoading: orderInvoicesLoading } =
    useGetPosOrderInvoicesQuery(resolvedOrderId, {
      skip: !resolvedOrderId,
    });

  // Mutations
  const [
    processInvoicePayment,
    { isLoading: isProcessingInvoice, error: invoiceError },
  ] = useProcessPosInvoicePaymentMutation();
  const [
    processOrderPayment,
    { isLoading: isProcessingOrder, error: orderError },
  ] = useProcessPosOrderPaymentMutation();
  const [sendSeatHeartbeat] = usePosSeatHeartbeatMutation();

  const isLoading = invoiceLoading || orderLoading || orderInvoicesLoading;
  const isProcessing = isProcessingInvoice || isProcessingOrder;
  const paymentError = invoiceError || orderError;
  const orderInvoices = Array.isArray(orderInvoicesData) ? orderInvoicesData : [];
  const printableInvoice = isInvoicePayment
    ? invoice
    : orderInvoices.find((entry) => entry?.invoiceId) || null;

  // Calculate payment amount
  const totalAmount = isInvoicePayment
    ? Number(invoice?.grandTotal || 0) - Number(invoice?.paidAmount || 0)
    : Number(order?.totals?.grandTotal || 0);
  const payableAmount = Math.max(
    Number(totalAmount || 0) + Number(selectedTipAmount || 0),
    0,
  );
  const invoiceTipTotal = Number(invoice?.tipTotal || 0);
  const orderTipTotal = Number(
    order?.totals?.tipTotal ?? order?.totals?.tip_total ?? 0,
  );

  React.useEffect(() => {
    if (!resolvedOrderId && invoice?.orderId) {
      setResolvedOrderId(invoice.orderId);
    }
  }, [resolvedOrderId, invoice?.orderId]);

  React.useEffect(() => {
    if (targetOrderId && targetOrderId !== resolvedOrderId) {
      setResolvedOrderId(targetOrderId);
    }
  }, [targetOrderId, resolvedOrderId]);

  React.useEffect(() => {
    setSelectedTipAmount(0);
  }, [invoiceId, resolvedOrderId]);

  // Keep draft seat session alive while payment screen is open
  React.useEffect(() => {
    if (!order?.seatId) return undefined;
    if (String(order?.status || "").toUpperCase() !== "DRAFT") return undefined;

    let isActive = true;
    const heartbeat = () => {
      if (!isActive) return;
      sendSeatHeartbeat(order.seatId).unwrap().catch(() => {});
    };

    heartbeat();
    const interval = setInterval(heartbeat, 45000);

    return () => {
      isActive = false;
      clearInterval(interval);
    };
  }, [order?.seatId, order?.status, sendSeatHeartbeat]);

  const handlePayment = async (method, amount, tipAmount = 0) => {
    setError(null);
    const normalizedAmount = normalizeMoneyAmount(amount);
    const normalizedTipAmount = normalizeMoneyAmount(tipAmount);

    try {
      if (isInvoicePayment) {
        const signature = buildPaymentSignature({
          scope: `INVOICE:${invoiceId}`,
          method,
          amount,
        });
        const attempt = nextIdempotencyState(
          lastInvoicePaymentAttemptRef.current,
          signature,
        );
        lastInvoicePaymentAttemptRef.current = attempt;

        await processInvoicePayment({
          invoiceId,
          payload: {
            method,
            amount: normalizedAmount,
            tipAmount: normalizedTipAmount,
          },
        }).unwrap();

        lastInvoicePaymentAttemptRef.current = null;
      } else {
        await processOrderPayment({
          orderId: resolvedOrderId,
          payload: {
            method,
            amount: normalizedAmount,
            tipAmount: normalizedTipAmount,
          },
        }).unwrap();
      }

      if (String(method || "").toUpperCase() === "CASH") {
        dispatch(api.util.invalidateTags(["TillSession", "Reconciliation"]));
      }

      setSuccess(true);
    } catch (err) {
      if (isInvoicePayment && lastInvoicePaymentAttemptRef.current) {
        lastInvoicePaymentAttemptRef.current = {
          ...lastInvoicePaymentAttemptRef.current,
          retryAllowed: isRetryablePaymentError(err),
        };
      }
      setError(getPosErrorMessage(err));
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-indigo-600 border-t-transparent"></div>
          <p className="mt-4 text-slate-600 dark:text-slate-400">
            Loading payment details...
          </p>
        </div>
      </div>
    );
  }

  if (!invoice && !order) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900">
        <Alert tone="error">Payment information not found</Alert>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-200 dark:from-slate-950 dark:via-slate-900 dark:to-slate-800">
      {/* Header */}
      <div className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl sticky top-0 z-10">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                disabled={isProcessing}
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                  Payment
                </h1>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {isInvoicePayment
                    ? `Invoice #${invoice.invoiceNo}`
                    : `Order #${order.orderNo}`}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Amount Due
              </p>
              <p className="text-3xl font-black text-indigo-600 dark:text-indigo-400">
                ${Math.max(totalAmount || 0, 0).toFixed(2)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Real-time Update Notification */}
      {realtimeUpdate && (
        <div className="fixed top-20 right-4 z-50 animate-slide-in-right">
          <Alert tone="success">
            Payment status updated: {realtimeUpdate.status}
          </Alert>
        </div>
      )}

      {/* Content */}
      <div className="px-6 py-6 pb-24">
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Success Message */}
          {success && (
            <div className="space-y-4">
              <Alert tone="success">
                <div className="flex items-center gap-2">
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  Payment processed successfully.
                </div>
              </Alert>

              <div className="rounded-xl border-2 border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                      Receipt Actions
                    </h2>
                    <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                      Print the paid invoice receipt or return to the POS screen.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <PrintReceiptAction
                      invoiceId={printableInvoice?.invoiceId || printableInvoice?.id}
                      invoiceNo={printableInvoice?.invoiceNo}
                      variant="secondary"
                      size="md"
                      disabled={!printableInvoice}
                    />
                    <Button
                      type="button"
                      size="md"
                      onClick={() => {
                        if (isInvoicePayment) {
                          navigate(
                            `/pos/orders/${invoice.orderId || resolvedOrderId}/split`,
                          );
                          return;
                        }
                        navigate("/pos");
                      }}
                    >
                      Done
                    </Button>
                  </div>
                </div>
                {!printableInvoice && (
                  <div className="mt-4">
                    <Alert tone="warning">
                      Receipt printing is not available because the invoice context could not be resolved yet.
                    </Alert>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <Alert tone="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Payment Details Card */}
          {!success && (
            <div className="rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
                Payment Details
              </h2>

              <div className="space-y-3">
                {isInvoicePayment ? (
                  <>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600 dark:text-slate-400">
                        Invoice Number
                      </span>
                      <span className="font-medium text-slate-900 dark:text-slate-100">
                        #{invoice.invoiceNo}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600 dark:text-slate-400">
                        Subtotal
                      </span>
                      <span className="font-medium text-slate-900 dark:text-slate-100">
                        ${invoice.subtotal.toFixed(2)}
                      </span>
                    </div>
                    {invoice.discountTotal > 0 && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600 dark:text-slate-400">
                          Discount
                        </span>
                        <span className="font-medium text-emerald-600 dark:text-emerald-400">
                          -${invoice.discountTotal.toFixed(2)}
                        </span>
                      </div>
                    )}
                    {invoice.taxTotal > 0 && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600 dark:text-slate-400">
                          Tax
                        </span>
                        <span className="font-medium text-slate-900 dark:text-slate-100">
                          ${invoice.taxTotal.toFixed(2)}
                        </span>
                      </div>
                    )}
                    {invoiceTipTotal > 0 && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600 dark:text-slate-400">
                          Tip
                        </span>
                        <span className="font-medium text-slate-900 dark:text-slate-100">
                          ${invoiceTipTotal.toFixed(2)}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600 dark:text-slate-400">
                        Total
                      </span>
                      <span className="font-medium text-slate-900 dark:text-slate-100">
                        ${invoice.grandTotal.toFixed(2)}
                      </span>
                    </div>
                    {invoice.paidAmount > 0 && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600 dark:text-slate-400">
                          Already Paid
                        </span>
                        <span className="font-medium text-emerald-600 dark:text-emerald-400">
                          ${invoice.paidAmount.toFixed(2)}
                        </span>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600 dark:text-slate-400">
                        Order Number
                      </span>
                      <span className="font-medium text-slate-900 dark:text-slate-100">
                        #{order.orderNo}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600 dark:text-slate-400">
                        Seat
                      </span>
                      <span className="font-medium text-slate-900 dark:text-slate-100">
                        {order.seatRef}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600 dark:text-slate-400">
                        Status
                      </span>
                      <OrderStatusBadge status={order.status} size="sm" />
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600 dark:text-slate-400">
                        Subtotal
                      </span>
                      <span className="font-medium text-slate-900 dark:text-slate-100">
                        ${order.totals.subTotal.toFixed(2)}
                      </span>
                    </div>
                    {order.totals.discount > 0 && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600 dark:text-slate-400">
                          Discount
                        </span>
                        <span className="font-medium text-emerald-600 dark:text-emerald-400">
                          -${order.totals.discount.toFixed(2)}
                        </span>
                      </div>
                    )}
                    {order.totals.tax > 0 && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600 dark:text-slate-400">
                          Tax
                        </span>
                        <span className="font-medium text-slate-900 dark:text-slate-100">
                          ${order.totals.tax.toFixed(2)}
                        </span>
                      </div>
                    )}
                    {orderTipTotal > 0 && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600 dark:text-slate-400">
                          Tip
                        </span>
                        <span className="font-medium text-slate-900 dark:text-slate-100">
                          ${orderTipTotal.toFixed(2)}
                        </span>
                      </div>
                    )}
                  </>
                )}

                <div className="pt-3 border-t-2 border-slate-200 dark:border-slate-700">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-slate-900 dark:text-slate-100">
                      Amount Due
                    </span>
                    <span className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                      ${payableAmount.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Payment Method Selector */}
          {!success && totalAmount > 0 && (
            <div className="rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6">
              <PaymentMethodSelector
                totalAmount={totalAmount}
                onPay={handlePayment}
                isLoading={isProcessing}
                error={paymentError}
                allowPartialPayment={isInvoicePayment}
                onTipChange={setSelectedTipAmount}
                tipConfig={tipConfig}
              />
            </div>
          )}

          {/* Already Paid */}
          {!success && totalAmount <= 0 && (
            <Alert tone="success">
              This {isInvoicePayment ? "invoice" : "order"} has already been
              paid in full.
            </Alert>
          )}
        </div>
      </div>

      {!success && (
        <div className="sticky bottom-0 z-20 border-t border-slate-200 dark:border-slate-700 bg-white/95 dark:bg-slate-900/95 backdrop-blur px-6 py-3">
          <div className="max-w-2xl mx-auto flex items-center justify-between gap-3">
            <div className="text-sm">
              <span className="text-slate-500 dark:text-slate-400">
                Amount Due:{" "}
              </span>
              <span className="font-bold text-indigo-600 dark:text-indigo-400">
                ${Math.max(totalAmount || 0, 0).toFixed(2)}
              </span>
            </div>
            <span className="text-xs text-slate-500 dark:text-slate-400">
              Select a payment method below
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payment;
